import {
    getSiteLedger,
    getVendorCatalog,
    reverseMovement,
    type MovementType,
} from '@api/vendor-portal';
import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import StatusPill, { type StatusTone } from '@components/ui/StatusPill';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, MultiSelect, Switch, Text, TextInput } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';

import { buildLedgerRows, isUndoable, type LedgerRow, reasonText } from './ledgerRows';

const PAGE_SIZE = 100;

const MOVEMENT_OPTIONS: Array<{ value: MovementType; label: string }> = [
    { value: 'RM_INWARD', label: 'Received' },
    { value: 'FG_PRODUCTION', label: 'Produced' },
    { value: 'RM_CONSUMPTION', label: 'Used in production' },
    { value: 'FG_DISPATCH', label: 'Dispatched' },
    { value: 'WASTAGE', label: 'Wasted' },
    { value: 'RM_RETURN', label: 'Returned to supplier' },
    { value: 'MARKED_BAD', label: 'Marked bad' },
    { value: 'ADJUSTMENT', label: 'Adjusted' },
    { value: 'OPENING_BALANCE', label: 'Opening balance' },
];

const PROVENANCE: Record<LedgerRow['provenance'], { label: string; tone: StatusTone }> = {
    CM: { label: 'This site', tone: 'neutral' },
    BRAND: { label: 'Brand entered', tone: 'info' },
    OPENING: { label: 'Opening', tone: 'warning' },
    SYNC: { label: 'System', tone: 'neutral' },
};

/** `dd-MMM-yyyy`, which reads unambiguously wherever the reader is. */
function formatDate(value: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
}

/**
 * Everything this site has recorded.
 *
 * A DECLARATION IS ONE LINE, expandable to the movements inside it. A run shown as five sibling
 * rows — finished goods here, four materials scattered among the day's other entries — describes
 * the database rather than the day, and it invites somebody to undo the finished goods while
 * leaving the material consumed.
 *
 * UNDO IS PRESENTED AS DELETE, and stored as a reversal. Those are not in conflict: a record
 * that can be erased is not a record, so the opposite entry is what actually happens — but from
 * the manufacturer's side a mistyped row simply went away, and a history where every correction
 * survives twice is one nobody scans. The undone pair is hidden by default and one switch away,
 * which is what somebody asking "what did I undo" needs.
 *
 * PROVENANCE IS ON EVERY ROW. A person at this site, the brand acting on their behalf, and a
 * seeded opening balance are three different kinds of claim, and a ledger that showed them
 * identically would let the weakest borrow the authority of the strongest.
 */
const VendorHistoryContainer: FC = () => {
    const { showToastSuccess, showToastError } = useToaster();

    const [movementTypes, setMovementTypes] = useState<MovementType[]>([]);
    const [itemIds, setItemIds] = useState<string[]>([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [showUndone, setShowUndone] = useState(false);
    const [page, setPage] = useState(1);
    const [undoing, setUndoing] = useState<string | null>(null);

    const { data: catalog } = useQuery(['vendor-catalog'], getVendorCatalog, {
        staleTime: 5 * 60 * 1000,
    });

    const query = useMemo(
        () => ({
            movementTypes: movementTypes.length > 0 ? movementTypes : undefined,
            itemIds: itemIds.length > 0 ? itemIds : undefined,
            dateFrom: dateFrom.trim() || undefined,
            dateTo: dateTo.trim() || undefined,
            page,
            pageSize: PAGE_SIZE,
        }),
        [movementTypes, itemIds, dateFrom, dateTo, page],
    );

    const { data, isLoading, refetch } = useQuery(
        ['vendor-ledger', JSON.stringify(query)],
        () => getSiteLedger(query),
        { keepPreviousData: true },
    );

    const rows = useMemo(
        () => buildLedgerRows(data?.entries ?? [], { showUndone }),
        [data, showUndone],
    );

    const itemOptions = useMemo(
        () => [
            ...(catalog?.materials ?? []).map((material) => ({
                value: material.itemId,
                label: `${material.itemId} · ${material.name}`,
            })),
            ...(catalog?.products ?? []).map((product) => ({
                value: product.itemId,
                label: `${product.itemId} · ${product.name}`,
            })),
        ],
        [catalog],
    );

    const handleUndo = async (row: LedgerRow) => {
        if (!row.reversibleEventId) return;
        setUndoing(row.reversibleEventId);
        try {
            const result = await reverseMovement(row.reversibleEventId);
            showToastSuccess({
                title: 'Undone',
                subtitle:
                    result.reversedCount > 1
                        ? `${result.reversedCount} movements were undone together — a run or a delivery cannot be half undone.`
                        : 'The entry no longer counts against your stock.',
            });
            void refetch();
        } catch (err: any) {
            showToastError({
                title: 'Could not undo it',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setUndoing(null);
        }
    };

    const columns = useMemo<DataSheetColumn<LedgerRow>[]>(
        () => [
            {
                key: 'date',
                header: 'Date',
                width: 120,
                sticky: true,
                render: (value: string) => formatDate(value),
                csvValue: (value: string) => value,
            },
            { key: 'label', header: 'What happened', width: 180, filterable: true },
            // A delivery's goods receipt number and the order it came on. Blank on everything
            // that is not a receipt, and on receipts recorded before numbering began.
            {
                key: 'grnId',
                header: 'GRN',
                width: 140,
                filterable: true,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'purchaseOrderId',
                header: 'PO',
                width: 130,
                filterable: true,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'itemId',
                header: 'Item',
                width: 220,
                filterable: true,
                render: (value: string, row) => row.itemName ?? value,
                valueAccessor: (row) => row.itemName ?? row.itemId,
                csvValue: (value: string, row) => row.itemName ?? value,
            },
            {
                key: 'batchCode',
                header: 'Batch',
                width: 140,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'quantity',
                header: 'Quantity',
                width: 110,
                align: 'right',
                sortable: true,
                // Signed and coloured: "40" against a consumption and a receipt are opposite
                // facts, and a reader scanning the column should not have to check the type.
                render: (value: number) => (
                    <Text
                        fz={13}
                        c={value < 0 ? semanticColors.critical.primary : neutralColors.textPrimary}
                    >
                        {value > 0 ? `+${value}` : value}
                    </Text>
                ),
            },
            {
                key: 'reasonCode',
                header: 'Reason',
                width: 220,
                filterable: true,
                render: (_value: string | null, row) => reasonText(row) ?? '—',
                valueAccessor: (row) => reasonText(row) ?? '',
                csvValue: (_value: string | null, row) => reasonText(row) ?? '',
            },
            {
                key: 'reference',
                header: 'Reference',
                width: 140,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'enteredByName',
                header: 'Entered by',
                width: 160,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'provenance',
                header: 'Source',
                width: 130,
                render: (value: LedgerRow['provenance']) => (
                    <StatusPill label={PROVENANCE[value].label} tone={PROVENANCE[value].tone} />
                ),
                csvValue: (value: LedgerRow['provenance']) => PROVENANCE[value].label,
            },
            {
                key: '_undo',
                header: '',
                width: 110,
                csvValue: () => '',
                render: (_value, row) => {
                    if (row.reversalOfId) {
                        return (
                            <Text fz={12} c={neutralColors.textMuted}>
                                Undoes another
                            </Text>
                        );
                    }
                    if (row.reversedByEventId) {
                        return <StatusPill label="Undone" tone="critical" />;
                    }
                    if (!isUndoable(row)) return null;

                    return (
                        <Button
                            size="xs"
                            variant={ButtonVariant.SUBTLE}
                            loading={undoing === row.reversibleEventId}
                            onClick={() => handleUndo(row)}
                        >
                            Undo
                        </Button>
                    );
                },
            },
        ],
        [undoing],
    );

    const total = data?.total ?? 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🧾"
                title="History"
                subtitle="Everything recorded at this site, and what can still be undone"
            />

            <Group gap={16} align="flex-end">
                <MultiSelect
                    label="What happened"
                    placeholder="Everything"
                    data={MOVEMENT_OPTIONS}
                    value={movementTypes}
                    onChange={(value) => {
                        setMovementTypes(value as MovementType[]);
                        setPage(1);
                    }}
                    clearable
                    w={260}
                />
                <MultiSelect
                    label="Item"
                    placeholder="Everything"
                    data={itemOptions}
                    value={itemIds}
                    onChange={(value) => {
                        setItemIds(value);
                        setPage(1);
                    }}
                    searchable
                    clearable
                    w={280}
                />
                <TextInput
                    label="From"
                    placeholder="YYYY-MM-DD"
                    value={dateFrom}
                    onChange={(e) => {
                        setDateFrom(e.currentTarget.value);
                        setPage(1);
                    }}
                    w={150}
                />
                <TextInput
                    label="To"
                    placeholder="YYYY-MM-DD"
                    value={dateTo}
                    onChange={(e) => {
                        setDateTo(e.currentTarget.value);
                        setPage(1);
                    }}
                    w={150}
                />
                <Switch
                    label="Show undone"
                    checked={showUndone}
                    onChange={(e) => setShowUndone(e.currentTarget.checked)}
                    mb={8}
                />
            </Group>

            <DataSheet<LedgerRow>
                data={rows}
                columns={columns}
                title="Movements"
                subtitle={isLoading ? 'Loading…' : `${total} recorded`}
                getRowKey={(row) => row.key}
                // A declaration expands to the movements inside it, which is also the unit it
                // undoes as.
                getSubRows={(row) => row.children}
                enableSearch
                searchPlaceholder="Search…"
                enableDownload
                downloadFilename="site-history.csv"
                maxHeight="auto"
                footer={
                    pageCount > 1 ? (
                        <Group justify="space-between">
                            <Text fz={12} c={neutralColors.textMuted}>
                                Page {page} of {pageCount}
                            </Text>
                            <Group gap={8}>
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.DEFAULT}
                                    disabled={page <= 1}
                                    onClick={() => setPage((current) => current - 1)}
                                >
                                    Previous
                                </Button>
                                <Button
                                    size="xs"
                                    variant={ButtonVariant.DEFAULT}
                                    disabled={page >= pageCount}
                                    onClick={() => setPage((current) => current + 1)}
                                >
                                    Next
                                </Button>
                            </Group>
                        </Group>
                    ) : undefined
                }
            />
        </div>
    );
};

export default VendorHistoryContainer;
